import { useAsyncExecutor2 } from "async-executor-hook";
import { differenceInDays, format } from "date-fns";
import { stringify } from "query-string";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormControl,
} from "rectangular-forms";
import { skip } from "rxjs";

import {
  inputMaxLengthFn,
  onlyDigits,
  WCalendar,
  WDropdown,
  WInputText,
  WInputTextDamPrefix,
  WMultipleTagInput,
  WMultiSelect,
} from "../../common/components/TemplateContainer/InputTemplates";
import { SaveButton } from "../../common/components/TemplateContainer/SaveButton";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import { removeNullProps } from "../../common/utils";
import { DATE_SELECT } from "../../utils/constants";
import { DAM_MAX_LENGTH } from "../../utils/valueStaticConstants";

export const DOCUMENT_TYPE_OPTION = [
  { value: "senasa", label: "SENASA", endpoint: "/dams/senasa-admin/" },
  {
    value: "electronic_mandate",
    label: "MANDATO ELECTRÓNICO",
    endpoint: "/dams/electronic-mandate-admin/",
  },
  {
    value: "third_party_billing_authorization",
    label: "FACTURACIÓN A TERCEROS",
    endpoint: "/volantes/third-party-billing-authorization-admin/",
  },
  {
    value: "agent_importer",
    label: "CRÉDITO DEL IMPORTADOR",
    endpoint: "/invoices/credit/",
  },
  {
    value: "agent_cargo",
    label: "CRÉDITO DEL AGENTE DE CARGA",
    endpoint: "/invoices/credit/",
  },
  {
    value: "third_party_agency",
    label: "CRÉDITO DE TERCERO",
    endpoint: "/invoices/credit/",
  },
];

export const REGIMEN_OPTION = [{ value: "10", label: "Imp. Definitiva" }];

export const STATUS_DOCUMENT = [
  { value: "not_required", label: "No requerido" },
  { value: "pending", label: "Pendiente" },
  { value: "approved", label: "Aprobado" },
  { value: "rejected", label: "Rechazado" },
  { value: "reviewing", label: "En revisión" },
];
export const STATUS_DOCUMENT_THIRD_PARTY = [
  { value: "not_required", label: "No requerido" },
  { value: "approved", label: "Aprobado" },
  { value: "rejected", label: "Rechazado" },
  { value: "reviewing", label: "En revisión" },
];
export const STATUS_PAYMENT_STATUS = [
  { value: "pending_validation", label: "PENDIENTE" },
  { value: "paid", label: "APROBADO" },
  { value: "rejected", label: "RECHAZADO" },
];
export const VOLANTE_TEST = [
  { value: "12345678", label: "12345678" },
  { value: "12345673", label: "12345673" },
  { value: "1234567", label: "1234567" },
];

const AdminDocumentValidationSearch = (props) => {
  const history = useHistory();
  const { params$ } = props;
  const formConfig = useFormConfig({
    createForm: (data) => {
      const documentType = new FormControl();
      const form = new FormGroup({
        documentType,
        agent_name: new FormControl(),
        from_date: new FormControl(),
        dam_num: new FormControl(),
        dam: new FormGroup({
          year: new FormControl(),
          num: new FormControl(null, Validators.minLength(6)),
          regimen: new FormControl(),
        }),

        senasa_status_option: new FormControl(),
        to_date: new FormControl(),
        electronic_mandate_status_option: new FormControl(),
        third_party_billing_authorization_status_option: new FormControl(),
        volante_nums: new FormControl(),
        payment_status_option: new FormControl(),
      });

      documentType.valueChanges.pipe(skip(1)).subscribe((documentType) => {
        const validDocument =
          documentType === "electronic_mandate" ||
          documentType === "third_party_billing_authorization" ||
          documentType === "agent_importer" ||
          documentType === "agent_cargo" ||
          documentType === "third_party_agency";

        if (validDocument) {
          form.patchValue({ agent_name: "", volante_nums: [] });
        }
        if (documentType === "senasa") {
          form.patchValue({
            agent_name: "",
            volante_nums: [],
            dam: { num: "" },
          });
        }
      });

      form.get("from_date").valueChanges.subscribe((from_date: Date) => {
        const toDate = new Date(form.get("to_date").value) || new Date();
        const diffDate = differenceInDays(toDate, new Date(from_date));
        if (diffDate < 1) {
          form.get("to_date").setValue(from_date);
        }
      });

      form.patchValue(data);
      return form;
    },
    onSubmit: (form) => {
      if (!form.valid) {
        return;
      }
      const data = form.value;
      const documentType = data.documentType;
      const from_date = data.from_date && format(data.from_date, "yyyy-MM-dd");
      const to_date = data.from_date && format(data.to_date, "yyyy-MM-dd");

      const dam =
        documentType === "senasa"
          ? {
              "dam.regimen": form.get("dam.regimen").value,
              "dam.num": form.get("dam.num").value,
              "dam.year": form.get("dam.year").value,
            }
          : {};
      const params = removeNullProps({
        ...data,
        dam: undefined,
        ...dam,
        from_date,
        to_date,
      });
      const pathname = history.location.pathname;
      history.push(`${pathname}?${stringify(params)}`);
    },
  });

  useAsyncExecutor2((params) => params, {
    params$,
    onSuccess: (params) => {
      formConfig.loadSucceed(params);
    },
  });

  const documentType = formConfig.form?.get("documentType")?.value;

  const senasaSelected = documentType === "senasa";
  const electronicMandateSelected = documentType === "electronic_mandate";
  const thirdPartySelected =
    documentType === "third_party_billing_authorization";

  const thirdPartyAgency =
    documentType === "third_party_agency" ||
    documentType === "agent_importer" ||
    documentType === "agent_cargo";
  const invoicePartySelected =
    documentType === "agent_importer" || documentType === "agent_cargo";

  const buttonShow =
    senasaSelected ||
    electronicMandateSelected ||
    thirdPartySelected ||
    thirdPartyAgency;

  const [damYearOptions] = useState(() => {
    return DATE_SELECT;
  });

  const damYear = formConfig.form?.get("dam.year").value;
  const damRegimen = formConfig.form?.get("dam.regimen").value;

  return (
    <>
      <WForm formConfig={formConfig}>
        <div
          className="p-grid"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div
            className={
              invoicePartySelected ? "p-col-6 p-md-4" : "p-col-6 p-md-4"
            }
          >
            <WFormControl name="documentType">
              <TemplateInputContainer label="Tipo de documento:">
                <WDropdown width="100%" options={DOCUMENT_TYPE_OPTION} />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className={
              invoicePartySelected ? "p-col-6 p-md-4" : "p-col-6 p-md-4"
            }
          >
            <WFormControl name="agent_name">
              <TemplateInputContainer label="Agente de aduana:">
                <WInputText width="100%" placeholder="Nombre del agente" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl name="from_date">
              <TemplateInputContainer label="Fecha desde:" color="#08082B">
                <WCalendar width="100%" dateFormat="dd/mm/yy" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl name="to_date">
              <TemplateInputContainer label="Fecha hasta:" color="#08082B">
                <WCalendar
                  width="100%"
                  minDate={
                    formConfig.form?.get("from_date")?.value || new Date()
                  }
                  dateFormat="dd/mm/yy"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid p-mt-1"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {senasaSelected && (
            <>
              <div className="p-col-6 p-md-3">
                <WFormControl name="senasa_status_option">
                  <TemplateInputContainer label="Estado:">
                    <WMultiSelect options={STATUS_DOCUMENT} width="100%" />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
              <div className="p-col-6 p-md-3">
                <WFormControl name="dam.year">
                  <TemplateInputContainer label="Año:">
                    <WDropdown width="100%" options={damYearOptions} />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
              <div className="p-col-6 p-md-2">
                <WFormControl name="dam.regimen">
                  <TemplateInputContainer label="Régimen:" color="#08082B">
                    <WDropdown width="100%" options={REGIMEN_OPTION} />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
              <div className="p-col-6 p-md-2">
                <WFormControl
                  name="dam.num"
                  errorMessages={{
                    minlength: "La dam debe de tener 6 dígitos",
                  }}
                >
                  <TemplateInputContainer label="Número DAM:" color="#08082B">
                    <WInputTextDamPrefix
                      width="100%"
                      placeholder="123456"
                      damYear={damYear}
                      damRegimenId={damRegimen}
                      maxLength={DAM_MAX_LENGTH}
                      onInput={inputMaxLengthFn}
                      onKeyPress={onlyDigits}
                    />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
            </>
          )}
          {electronicMandateSelected && (
            <div className="p-col-6 p-md-8">
              <WFormControl name="electronic_mandate_status_option">
                <TemplateInputContainer label="Estado:">
                  <WMultiSelect options={STATUS_DOCUMENT} width="100%" />
                </TemplateInputContainer>
              </WFormControl>
            </div>
          )}
          {thirdPartySelected && (
            <>
              <div className="p-col-6 p-md-4">
                <WFormControl name="third_party_billing_authorization_status_option">
                  <TemplateInputContainer label="Estado:">
                    <WMultiSelect
                      options={STATUS_DOCUMENT_THIRD_PARTY}
                      width="100%"
                    />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
              <div className="p-col-6 p-md-4">
                <WFormControl name="volante_nums">
                  <TemplateInputContainer label="Volantes:">
                    <WMultipleTagInput />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
            </>
          )}
          {thirdPartyAgency && (
            <>
              <div className="p-col-6 p-md-4">
                <WFormControl name="payment_status_option">
                  <TemplateInputContainer label="Estado:">
                    <WMultiSelect
                      options={STATUS_PAYMENT_STATUS}
                      width="100%"
                    />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
              <div className="p-col-6 p-md-4">
                <WFormControl name="volante_nums">
                  <TemplateInputContainer label="Volantes:">
                    <WMultipleTagInput />
                  </TemplateInputContainer>
                </WFormControl>
              </div>
            </>
          )}
          {buttonShow && (
            <div className="p-col">
              <div style={{ marginBottom: "1.8rem", height: "1.4rem" }} />
              <SaveButton
                label="Aplicar filtros"
                width="100%"
                height="5rem"
                backgroundColor="#002E6C"
              />
            </div>
          )}
        </div>
      </WForm>
    </>
  );
};

export default AdminDocumentValidationSearch;
